import { gridSize } from '@/shared/constants/ui.constants';
import { Theme } from '@emotion/react';
import { Autocomplete, Grid, SxProps, TextField } from '@mui/material';
import { Control, Controller, FieldError } from 'react-hook-form';
import './CustomAutocomplete.css';

export type CustomAutocompleteProps<T> = {
  name: string;
  loadingText?: string;
  label: string;
  disabled?: boolean;

  options: T[];
  valueKey: keyof T;
  actualValueKey?: keyof T;
  optionLabelForEdit?: string;
  isLoadingData: boolean;
  onChangeValue?: (value: any) => void;
  onChangeRawValue?: (value: T) => void;

  error: FieldError | undefined;
  helperText: React.ReactNode;
  required?: boolean;

  textFieldKey?: string;
  defaultValue: string | number | null | undefined;
  control: Control<any, any>;

  size?: {
    xs: number;
    sm: number;
    md: number;
    lg: number;
  };
  disableClearable?: boolean;

  sxTextField?: SxProps<Theme>;
  endAdornmentInput?: React.ReactNode;

  //send with enter
  enableToSubmitWithEnter?: boolean;
  handleSubmit?: () => void;
  isOptionEqualToValue?: any;
};

function CustomAutocomplete<T>({
  name,
  options,
  isLoadingData,
  error,
  textFieldKey,
  defaultValue,
  control,
  loadingText = 'Cargando...',
  optionLabelForEdit,
  valueKey,
  helperText,
  required = true,
  label,
  actualValueKey,
  onChangeValue,
  onChangeRawValue,
  size = gridSize,
  disabled = false,
  disableClearable = false,
  endAdornmentInput,

  //send with enter
  enableToSubmitWithEnter = false,
  handleSubmit = () => console.log("nothing happen"),
  isOptionEqualToValue,
}: Readonly<CustomAutocompleteProps<T>>) {

  return (
    <Grid item {...size}>
      <Controller

        name={name}
        control={control}
        key={textFieldKey ?? 'default'}
        defaultValue={defaultValue ?? ''}
        render={({ field }) => {
          const onChange = (_event: any, data: any) => {
            const selectedValue = data?.[actualValueKey ?? valueKey] ?? '';
            field.onChange(selectedValue);
            onChangeValue && onChangeValue(selectedValue); // label
            onChangeRawValue && onChangeRawValue(data); // T
          };

          const onKeyDown = (_event: any) => {
            enableToSubmitWithEnter ?
              _event.key === 'Enter' ? (
                // console.log('Enter key pressed')
                handleSubmit()
              )
                : null
              : null
          }

          return (
            <Autocomplete
              {...field}
              popupIcon={null}
              disabled={disabled}
              //freeSolo
              noOptionsText={'No hay opciones'}
              loading={isLoadingData}
              loadingText={loadingText}
              options={options}
              disableClearable={disableClearable}
              {
                ...isOptionEqualToValue && {
                  isOptionEqualToValue: isOptionEqualToValue
                }
              }
              getOptionLabel={(option: any) =>
                option?.[valueKey]?.toString() ??
                options
                  .find(opt => opt[actualValueKey ?? valueKey] === option)
                  ?.[valueKey]?.toString() ??
                optionLabelForEdit?.toString() ??
                defaultValue ?? ''
              }
              renderInput={params => (
                <TextField
                  {...params}
                  InputProps={{
                    ...params.InputProps,
                    ...(endAdornmentInput && {
                      endAdornment: endAdornmentInput,
                    }),
                  }}
                  autoComplete='off'
                  label={label}
                  variant="outlined"
                  error={!!error}
                  helperText={helperText}
                  required={required}
                  sx={{
                    '& .css-1i3ux3q-MuiInputBase-input-MuiOutlinedInput-input.Mui-disabled':
                    {
                      opacity: 0.8,
                      WebkitTextFillColor: 'black',
                    },
                  }}
                />
              )}
              onChange={onChange}
              onKeyDown={onKeyDown}
            />
          );
        }}
      />
    </Grid>
  );
}

export default CustomAutocomplete;